import * as React from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'
import { IVariantIndexData } from '@/@core/services/app.service'
import {
  Chip,
  IconButton,
  InputBase,
  Paper,
  Stack,
  styled,
  Tooltip,
  TooltipProps
} from '@mui/material'
import Index from '../Index'
import { judgeAcmg } from '@/@core/layouts/utils'
import { switchVariantClass } from '@/@core/utils/utils'
import SearchIcon from '@mui/icons-material/Search'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

export default function CaseNumberInfo(props: { value: any }) {
  const { t } = useTranslation()
  const [value, setValue] = React.useState(0)
  const [searchValue, setSearchValue] = React.useState('')
  const gnomadsColumns = [
    {
      id: 'id',
      label: 'id',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'chr',
      label: 'chr',
      format: ((value: any) => {
        return <Typography fontSize={12}>{`Chr${value}`}</Typography>
      }) as any
    },
    {
      id: 'start',
      label: 'start',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'end',
      label: 'end',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'popmax_af',
      label: 'popmax_af',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'qc_flag',
      label: 'qc_flag',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'overlap_rate',
      label: 'overlap_rate',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'type',
      label: 'type',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    }
  ]
  const clinvarsColumns = [
    {
      id: 'id',
      label: 'id',
      format: ((value: any) => {
        return (
          <Typography
            onClick={() => {
              window.open(`https://www.ncbi.nlm.nih.gov/clinvar/${value}`)
            }}
            fontSize={12}
            sx={{
              cursor: 'pointer',
              color: 'primary.main'
            }}
          >
            {value}
          </Typography>
        )
      }) as any
    },
    {
      id: 'chr',
      label: 'chr',
      format: ((value: any) => {
        return <Typography fontSize={12}>{`Chr${value}`}</Typography>
      }) as any
    },
    {
      id: 'start',
      label: 'start',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'end',
      label: 'end',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'interpretation',
      label: 'interpretation',
      format: ((value: any) => {
        return (
          <Typography fontSize={12}>{`${
            value && value != '.'
              ? value
                  .split(' ')
                  .map((word: any) => {
                    if (word.includes('Uncertain')) return `VU`
                    if (word.includes('null')) return ``
                    else return word[0].toUpperCase()
                  })
                  .join('')
              : '.'
          }`}</Typography>
        )
      }) as any
    },
    {
      id: 'genes',
      label: 'genes',
      format: ((value: any) => {
        const genes = value && Array.isArray(value) ? value.join(',') : ''

        return (
          <Tooltip title={genes}>
            <Typography
              fontSize={12}
              sx={{
                maxWidth: '250px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
              }}
            >
              {genes}
            </Typography>
          </Tooltip>
        )
      }) as any
    },
    {
      id: 'condition',
      label: 'condition',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'overlap_rate',
      label: 'overlap_rate',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'type',
      label: 'type',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    }
  ]
  const decipherCnvInfosColumns = [
    {
      id: 'patient_id',
      label: 'patient_id',
      format: ((value: any) => {
        return (
          <Typography
            onClick={() => {
              window.open(
                `https://www.deciphergenomics.org/patient/${value}/overview/general`
              )
            }}
            fontSize={12}
            sx={{
              cursor: 'pointer',
              color: 'primary.main'
            }}
          >
            {value}
          </Typography>
        )
      }) as any
    },
    {
      id: 'chr',
      label: 'chr',
      format: ((value: any) => {
        return <Typography fontSize={12}>{`Chr${value}`}</Typography>
      }) as any
    },
    {
      id: 'start',
      label: 'start',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'end',
      label: 'end',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'variant_class',
      label: 'variant_class',
      format: ((value: any) => {
        return (
          <Typography fontSize={12}>{`${switchVariantClass(
            value
          )}`}</Typography>
        )
      }) as any
    },
    {
      id: 'inheritance',
      label: 'inheritance',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'Coverage',
      label: 'Coverage',
      align: 'left',
      format: ((value: any) => {
        return (
          <Typography fontSize={12}>{value ? value.toFixed(2) : ''}</Typography>
        )
      }) as any
    },
    {
      id: 'genotype',
      label: 'genotype',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'pathogenicity',
      label: 'pathogenicity',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    }
  ]
  const decipherDgvInfosColumns = [
    {
      id: 'id',
      label: 'id',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'chr',
      label: 'chr',
      format: ((value: any) => {
        return <Typography fontSize={12}>{`Chr${value}`}</Typography>
      }) as any
    },
    {
      id: 'start',
      label: 'start',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'end',
      label: 'end',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'inner_start',
      label: 'inner_start',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'inner_end',
      label: 'inner_end',
      align: 'left',
      format: ((value: any) => {
        return <Typography fontSize={12}>{value}</Typography>
      }) as any
    },
    {
      id: 'variant_class',
      label: 'variant_class',
      format: ((value: any) => {
        return (
          <Typography fontSize={12}>{`${switchVariantClass(
            value
          )}`}</Typography>
        )
      }) as any
    },
    {
      id: 'Coverage',
      label: 'Coverage',
      align: 'left',
      format: ((value: any) => {
        return (
          <Typography fontSize={12}>{value ? value.toFixed(2) : ''}</Typography>
        )
      }) as any
    },
    {
      id: 'acc',
      label: 'acc',
      align: 'left',
      format: ((value: any) => {
        return (
          <Typography
            onClick={() => {
              window.open(
                `http://dgv.tcag.ca/gb2/gbrowse_details/dgv2_hg38?name=${value}`
              )
            }}
            fontSize={12}
            sx={{
              cursor: 'pointer',
              color: 'primary.main'
            }}
          >
            {value}
          </Typography>
        )
      }) as any
    },
    {
      id: 'population_allele_counts',
      label: t('frequency'),
      align: 'left',
      format: ((value: any, row: any) => {
        return (
          <Box>
            <Typography fontSize={12}>
              {`Total: ${((row.allele_count / row.allele_number) * 100).toFixed(
                2
              )}%(${row.allele_count}/${row.allele_number})`}
            </Typography>

            <Typography fontSize={12}>
              {`African: ${
                row.population_allele_counts &&
                row.population_allele_counts.African
                  ? row.population_allele_counts.African
                  : '-'
              }`}
            </Typography>

            <Typography fontSize={12}>
              {`Asian: ${
                row.population_allele_counts &&
                row.population_allele_counts.Asian
                  ? row.population_allele_counts.Asian
                  : '-'
              }`}
            </Typography>

            <Typography fontSize={12}>
              {`European: ${
                row.population_allele_counts &&
                row.population_allele_counts.European
                  ? row.population_allele_counts.European
                  : '-'
              }`}
            </Typography>

            <Typography fontSize={12}>
              {`Unknown: ${
                row.population_allele_counts &&
                row.population_allele_counts.Unknown
                  ? row.population_allele_counts.Unknown
                  : '-'
              }`}
            </Typography>
          </Box>
        )
      }) as any
    }
  ]

  const filterObjectsByString = (data: any[], searchString: string) => {
    if (!searchString) return data

    const searchLower = searchString.trim().toLowerCase()

    return data.filter((item) =>
      Object.values(item).some((value) => {
        if (typeof value === 'string')
          return (
            value.toLowerCase() === searchLower ||
            `${
              value && value != '.'
                ? value
                    .split(' ')
                    .map((word: any) => {
                      if (word.includes('Uncertain')) return `VU`
                      if (word.includes('null')) return ``
                      else return word[0].toUpperCase()
                    })
                    .join('')
                : '.'
            }`.toLowerCase() === searchLower
          )
        else if (Array.isArray(value))
          return value.some(
            (item: string) => item.toLowerCase() === searchLower
          )
        else if (typeof value === 'number')
          return value.toString() === searchLower
        else value === searchString
      })
    )
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }
  const gnomads = React.useMemo(() => {
    return filterObjectsByString(props.value?.gnomads || [], searchValue)
  }, [props.value?.gnomads, searchValue])

  const clinvars = React.useMemo(() => {
    return filterObjectsByString(props.value?.clinvars || [], searchValue)
  }, [props.value?.clinvars, searchValue])

  const decipherCnvs = React.useMemo(() => {
    return filterObjectsByString(
      props.value?.decipherCnvInfos || [],
      searchValue
    )
  }, [props.value?.decipherCnvInfos, searchValue])

  const decipherDgvInfos = React.useMemo(() => {
    return filterObjectsByString(
      props.value?.decipherDgvInfos || [],
      searchValue
    )
  }, [props.value?.decipherDgvInfos, searchValue])

  return (
    <Box sx={{ width: '1400px' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            sx={{
              '& .MuiButtonBase-root': {
                textTransform: 'capitalize'
              }
            }}
          >
            <Tab label={t('gnomadSV')} {...a11yProps(0)} />

            <Tab label={t('clinvarSV')} {...a11yProps(1)} />

            <Tab label={t('decipherCnv')} {...a11yProps(2)} />

            <Tab label={t('Dgv')} {...a11yProps(3)} />
          </Tabs>

          <Box>
            <Paper
              component="form"
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                maxWidth: 400,
                height: '2.5rem'
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder={t('Search')}
                inputProps={{ 'aria-label': 'search' }}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                }}
              />

              <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
                <SearchIcon />
              </IconButton>
            </Paper>
          </Box>
        </Stack>
      </Box>

      <CustomTabPanel value={value} index={0}>
        <Index
          key={gnomads.length}
          columns={gnomadsColumns as any}
          rows={gnomads}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={1}>
        <Index
          key={clinvars.length}
          columns={clinvarsColumns as any}
          rows={clinvars}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={2}>
        <Index
          key={decipherCnvs.length}
          columns={decipherCnvInfosColumns as any}
          rows={decipherCnvs}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={3}>
        <Index
          key={decipherDgvInfos.length}
          columns={decipherDgvInfosColumns as any}
          rows={decipherDgvInfos}
        />
      </CustomTabPanel>
    </Box>
  )
}
